import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Receipt, CheckCircle } from 'lucide-react';
import ReceiptUploadStep from './ReceiptUploadStep';
import ExpenseFormWithOCR from './ExpenseFormWithOCR';
import ModernLayout from './ModernLayout';
import { ModernCard } from './ModernCards';

interface OCRData {
  extracted_amount: number | null | undefined;
  extracted_date: string | null | undefined;
  extracted_vendor: string | null | undefined;
  extracted_invoice_id: string | null | undefined;
  category_id?: number | null;
  policy_id?: number | null;
  category?: {
    id: number;
    name: string;
    description?: string;
  } | null;
  policy?: {
    id: number;
    name: string;
    description?: string;
  } | null;
}

type Step = 'receipt' | 'expense';

const CreateExpenseWithReceipt: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('receipt');
  const [receiptId, setReceiptId] = useState<number | null>(null);
  const [ocrData, setOcrData] = useState<OCRData | null>(null);

  const handleReceiptAnalyzed = (id: number, data: OCRData) => {
    setReceiptId(id);
    setOcrData(data);
  };

  const handleNext = () => {
    setCurrentStep('expense');
  };

  const handleBack = () => {
    setCurrentStep('receipt');
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <ModernLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 font-display">Create Expense from Receipt</h1>
                <p className="text-gray-600 mt-1">
                  Upload a receipt and we'll help you create an expense with the extracted information
                </p>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <ModernCard className="p-6">
            <div className="flex items-center justify-center">
              <div className={`flex items-center ${currentStep === 'receipt' ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  currentStep === 'receipt' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentStep === 'receipt' ? <Receipt className="h-5 w-5" /> : '1'}
                </div>
                <span className="ml-3 font-medium">Upload Receipt</span>
              </div>
              
              <div className={`flex-1 h-0.5 mx-6 ${
                currentStep === 'expense' ? 'bg-green-600' : 'bg-gray-200'
              }`} />
              
              <div className={`flex items-center ${currentStep === 'expense' ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                  currentStep === 'expense' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentStep === 'expense' ? <CheckCircle className="h-5 w-5" /> : '2'}
                </div>
                <span className="ml-3 font-medium">Create Expense</span>
              </div>
            </div>
          </ModernCard>
        </div>

        {/* Content */}
        {currentStep === 'receipt' && (
          <ReceiptUploadStep
            onReceiptAnalyzed={handleReceiptAnalyzed}
            onNext={handleNext}
          />
        )}
        
        {currentStep === 'expense' && receiptId && ocrData && (
          <ExpenseFormWithOCR
            receiptId={receiptId}
            ocrData={ocrData}
            onBack={handleBack}
          />
        )}
      </div>
    </ModernLayout>
  );
};

export default CreateExpenseWithReceipt;
